import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  EnvelopeSimple,
  CaretRight,
  Trophy,
  ShieldCheck,
  ChatCircleText,
  CheckCircle,
} from "@phosphor-icons/react";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const STAGE_STYLES = {
  cold:      { label: "Cold",       cls: "bg-exodus-surface text-exodus-muted border-exodus-border" },
  contacted: { label: "Contacted",  cls: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  proposed:  { label: "Proposed",   cls: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30" },
  bound:     { label: "Bound",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  lost:      { label: "Lost",       cls: "bg-red-500/10 text-red-300 border-red-500/30" },
};
const STAGES = ["cold", "contacted", "proposed", "bound", "lost"];

export default function AdminPipeline() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/pipeline");
      setData(r.data);
      if (!selectedEmail && r.data.users?.length) setSelectedEmail(r.data.users[0].email);
    } catch {
      toast.error("Could not load pipeline. Make sure you're signed in as advisor.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let users = data?.users || [];
    if (stageFilter !== "all") users = users.filter((u) => u.best_stage === stageFilter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      users = users.filter(
        (u) =>
          (u.email || "").toLowerCase().includes(needle) ||
          (u.full_name || "").toLowerCase().includes(needle) ||
          u.scenarios.some((s) => (s.label || "").toLowerCase().includes(needle))
      );
    }
    return users;
  }, [q, data, stageFilter]);

  const selected = filtered.find((u) => u.email === selectedEmail) || filtered[0];

  const updateStage = async (scenarioId, newStage, notes) => {
    try {
      await api.patch(`/admin/pipeline/${scenarioId}`, { status: newStage, notes });
      toast.success(`Marked ${STAGE_STYLES[newStage].label}`);
      // Optimistic local update
      setData((d) => ({
        ...d,
        users: d.users.map((u) => ({
          ...u,
          scenarios: u.scenarios.map((s) =>
            s.id === scenarioId || s._id === scenarioId
              ? { ...s, pipeline_status: newStage, advisor_notes: notes ?? s.advisor_notes }
              : s
          ),
        })),
      }));
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-pipeline-page">
        <div className="mb-8">
          <p className="eyebrow"><span className="gold-divider" />Admin · Carrier Pipeline</p>
          <h1 className="font-display text-4xl mt-3">Every scenario, every stage.</h1>
          <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
            Ranked carrier comparisons your app users have saved. Move each one through the pipeline as you work it. Coaching briefs already landed in your inbox.
          </p>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <Kpi label="Total scenarios" value={data.total_scenarios} highlight />
            <Kpi label="App users" value={data.total_users} highlight />
            {STAGES.map((s) => (
              <Kpi
                key={s}
                label={STAGE_STYLES[s].label}
                value={data.stage_counts?.[s] || 0}
                onClick={() => setStageFilter(stageFilter === s ? "all" : s)}
                active={stageFilter === s}
              />
            ))}
          </div>
        )}

        {/* Search + filter reset */}
        <div className="flex items-center gap-3 mb-6">
          <div className="card-surface flex items-center gap-3 px-4 py-2 flex-1">
            <MagnifyingGlass size={16} className="text-exodus-muted" />
            <input
              data-testid="pipeline-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, name, or scenario label…"
              className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-exodus-muted/70"
            />
          </div>
          {stageFilter !== "all" && (
            <button
              data-testid="pipeline-clear-filter"
              onClick={() => setStageFilter("all")}
              className="text-xs eyebrow text-exodus-muted hover:text-exodus-gold px-3"
            >
              Clear filter
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-exodus-muted text-sm">Loading pipeline…</p>
        ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — user list */}
          <div className="lg:col-span-4 card-surface divide-y divide-exodus-border max-h-[70vh] overflow-auto">
            {filtered.length === 0 ? (
              <p className="p-6 text-exodus-muted text-sm">No users match those filters.</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.email}
                  data-testid={`pipeline-user-${u.email}`}
                  onClick={() => setSelectedEmail(u.email)}
                  className={`w-full px-5 py-4 flex items-center gap-3 text-left transition-colors ${
                    selected?.email === u.email
                      ? "bg-exodus-elevated border-l-2 border-exodus-gold"
                      : "hover:bg-exodus-elevated"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base truncate">{u.full_name || u.email}</p>
                    <p className="text-xs text-exodus-muted truncate">{u.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <StagePill stage={u.best_stage} small />
                      <span className="text-[10px] gold-text">
                        {u.scenario_count} scenario{u.scenario_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <CaretRight size={14} className="text-exodus-muted shrink-0" />
                </button>
              ))
            )}
          </div>

          {/* RIGHT — scenarios */}
          <div className="lg:col-span-8 space-y-4">
            {selected ? (
              <>
                <div className="card-surface p-6">
                  <p className="eyebrow text-xs">Client</p>
                  <h2 className="font-display text-2xl mt-1">{selected.full_name || "—"}</h2>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-exodus-muted">
                    <span><EnvelopeSimple size={12} className="inline mr-1" />{selected.email}</span>
                    <span>Signed up {fmtDate(selected.signed_up_at)}</span>
                    <span>Last seen {fmtDate(selected.last_login)}</span>
                  </div>
                </div>

                {selected.scenarios.map((s, idx) => (
                  <ScenarioCard
                    key={s.id || s._id || idx}
                    scenario={s}
                    onUpdate={(stg, notes) => updateStage(s.id || s._id, stg, notes)}
                  />
                ))}
              </>
            ) : (
              <div className="card-surface p-10 text-center text-exodus-muted text-sm">Select a user.</div>
            )}
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
function ScenarioCard({ scenario: s, onUpdate }) {
  const [notes, setNotes] = useState(s.advisor_notes || "");
  const [dirty, setDirty] = useState(false);
  const currentStage = s.pipeline_status || "cold";
  const stageStyle = STAGE_STYLES[currentStage] || STAGE_STYLES.cold;

  return (
    <div data-testid={`pipeline-scenario-${s.id || s._id}`} className="card-surface p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow text-[10px]">{s.product?.replace(/_/g, " ") || "—"}</p>
          <h3 className="font-display text-xl mt-1">{s.label}</h3>
          <p className="text-xs text-exodus-muted mt-1">{fmtDate(s.created_at)}</p>
        </div>
        <StagePill stage={currentStage} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <MicroStat label="Age" v={`${s.age} → ${s.target_retirement_age}`} />
        <MicroStat label="Sex / health" v={`${s.sex} · ${(s.health_class || "").replace("_", " ")}`} />
        <MicroStat label="Monthly" v={fmt(s.monthly_premium || 0)} />
        <MicroStat label="Goal" v={s.goal} />
      </div>

      {/* Top 3 carriers */}
      {s.top_carriers?.length > 0 && (
        <div className="mt-5 border-t border-exodus-border pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} weight="fill" className="text-exodus-gold" />
            <p className="text-xs eyebrow text-exodus-muted">Top 3 ranked carriers</p>
          </div>
          <div className="space-y-2">
            {s.top_carriers.slice(0, 3).map((c, i) => (
              <div key={c.slug} className="flex items-center justify-between gap-3 text-sm border-l-2 border-exodus-gold-deep pl-3 py-1">
                <div className="min-w-0">
                  <span className="gold-text font-display mr-2">#{i + 1}</span>
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-exodus-muted ml-2">{c.am_best}</span>
                </div>
                <div className="text-right text-xs shrink-0">
                  <span className="gold-text">{fmt(c.projected_cash_value)}</span>
                  {c.projected_monthly_income > 0 && (
                    <span className="text-exodus-muted ml-2">·  {fmt(c.projected_monthly_income)}/mo</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stage advancer */}
      <div className="mt-5 pt-4 border-t border-exodus-border">
        <p className="text-xs eyebrow text-exodus-muted mb-3 flex items-center gap-2">
          <ShieldCheck size={12} weight="fill" className="text-exodus-gold" />
          Pipeline stage
        </p>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((stg) => (
            <button
              key={stg}
              data-testid={`stage-btn-${stg}-${s.id || s._id}`}
              onClick={() => onUpdate(stg, notes)}
              className={`text-xs px-3 py-2 border transition-colors ${
                currentStage === stg
                  ? STAGE_STYLES[stg].cls + " ring-1 ring-exodus-gold"
                  : "border-exodus-border text-exodus-muted hover:text-exodus-text hover:border-exodus-gold-deep"
              }`}
            >
              {STAGE_STYLES[stg].label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4">
        <p className="text-xs eyebrow text-exodus-muted mb-2 flex items-center gap-2">
          <ChatCircleText size={12} weight="fill" />
          Advisor notes
        </p>
        <textarea
          data-testid={`notes-${s.id || s._id}`}
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
          placeholder="Call outcome, objections, next step…"
          rows={2}
          className="w-full bg-exodus-bg border border-exodus-border p-3 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors resize-none"
        />
        {dirty && (
          <button
            data-testid={`save-notes-${s.id || s._id}`}
            onClick={() => { onUpdate(currentStage, notes); setDirty(false); }}
            className="mt-2 text-xs gold-text hover:underline flex items-center gap-1"
          >
            <CheckCircle size={12} weight="fill" /> Save notes
          </button>
        )}
      </div>
    </div>
  );
}

function StagePill({ stage, small }) {
  const style = STAGE_STYLES[stage] || STAGE_STYLES.cold;
  return (
    <span
      data-testid={`stage-pill-${stage}`}
      className={`inline-block text-[10px] eyebrow tracking-wider border ${style.cls} ${small ? "px-2 py-0.5" : "px-3 py-1"}`}
    >
      {style.label}
    </span>
  );
}

function Kpi({ label, value, highlight, onClick, active }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`card-surface p-4 text-left transition-colors ${onClick ? "hover:bg-exodus-elevated cursor-pointer" : ""} ${active ? "ring-1 ring-exodus-gold" : ""}`}
    >
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className={`font-display text-2xl mt-1 ${highlight ? "gold-text" : ""}`}>{value}</p>
    </Comp>
  );
}

function MicroStat({ label, v }) {
  return (
    <div>
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className="text-sm font-display mt-1">{v}</p>
    </div>
  );
}
